// src/portal-app/insights/CostManagementScreen.js
//
// W2 Task 8 — Cost Management, the third native Insights surface.
// Phone-first rebuild of src/pages/portal/reporting/CostManagementPage.js
// over the SAME data services, assembled from the Task-5 kit.
//
// Data — reused UNCHANGED from services/api.js:
//   reportingService.getCostsV2(start, end)          the main payload
//   reportingService.getCostNarrativeV2(start, end)  enhancement — fails soft
//
// Zones: hero Revenue + DeltaPill → AI narrative → cost-data-health banner
// (flagged products push Product Detail) → KPI grid → "what these figures
// mean" (narrative kpi_descriptions, progressive disclosure) → P&L cost
// flow (ShareBar, W4 Task 16 — see below; NOT a pie — pre-approved)
// → overhead vs daily expenses (CondensedRow, W4 Task 16, SEPARATE
// per-group denominators) → price-variance alerts → daily cost pattern
// (multi-series TrendChart, interior closed days zero-filled) → insights.
//
// ⚠️ /costs KPIs carry NEITHER is_percentage NOR unit — "Gross Margin %" /
//    "Net Margin %" are percent purely by name; normaliseKpi owns that.
// ⚠️ The web's KPI-description key derivation leaves a trailing underscore
//    ("gross_margin_") and never matches the narrative's "gross_margin" —
//    descKeyFor() below trims it. Do not re-import the web's formula.
// ⚠️ gold.daily_summary has no rows for closed days — daily_costs is
//    zero-filled through the kit's fillDailyGaps before TrendChart (which
//    spaces points by index and would smooth the gap away).
//
// Deliberately dropped from the web page (desktop-only): KPI flip cards
// (kpi_descriptions moved into a disclosure card instead), the Recharts
// stacked-area chart's spike-threshold reference line (no kit primitive;
// flagged for a future kit addition), per-zone subtitle prose, and the
// deep-link into the Products *management* page from flagged rows (web
// navigation with no native destination — rows push the read-only Product
// Detail screen instead).
//
// W4 Task 16 (P6 — spec §5, conservative pass; NO structural rewrite, zone
// order unchanged; spec §7 names this screen specifically: "ShareBar
// replaces the P&L strip"):
//   • hero already carried the P4 GlassCard-veil treatment (HeroValue) —
//     confirmed, not re-authored. Kept as Revenue + delta: the web page's
//     own hero is `kpis[0]` (CostManagementPage.js:328), and /costs always
//     puts Revenue first — this native screen already matched that;
//   • ShareBar (shell/kit) REPLACES StackedShareBar (insights/kit) for the
//     Cost Flow section — this is THE central change Task 3A exists for.
//     StackedShareBar's legend maps `positive` for BOTH the bar AND the
//     legend (insights/kit.js:1057-1094), so a loss period's negative
//     net_profit vanished from the display entirely — the worst failure
//     mode for a P&L. ShareBar's Task 3A contract computes bar-segment
//     WIDTHS from positive segments only (so they always sum to 100%)
//     while the LEGEND renders every segment, including negative ones,
//     with its real signed value (shell/kit/ShareBar.js:25-29, 52).
//     Verified against this screen's actual P&L identity (reporting_v2.py
//     _cost_aggregates: net_profit = revenue − cogs − opex, so
//     cogs + opex + net_profit == revenue exactly): in a PROFIT period all
//     three segments are ≥0 and sum to revenue, so the bar's positive-only
//     widths are already revenue-anchored proportions with no denominator
//     needed. In a LOSS period (net_profit < 0) the bar draws only
//     COGS + OpEx — their split of total spend, not share of revenue — but
//     ShareBar's legend states amounts, never a %, so there is no
//     contradiction with cost_flow_narrative below (which states the true
//     revenue-anchored cogs_pct/opex_pct/net_margin_pct in words,
//     independent of what the bar itself draws). No denominator prop
//     exists on ShareBar (unlike the StackedShareBar it replaces) because
//     none is needed — see buildFlowSegments() and its own tests for the
//     profit / loss / all-negative cases;
//   • CondensedRow: TWO lists on this screen genuinely fit its
//     name/value/secondary/pill contract — the health banner's flagged
//     products (name, revenue value, units secondary, cost-source pill —
//     same shape ProductIntelligenceScreen's full-menu rows already use)
//     and the overhead/daily expense-category breakdowns (name, cost
//     value, "% of total" secondary). Nothing dropped: units, cost_source
//     and pct all still render, just reshaped from BarList's inline
//     caption / cms-flag-row markup into CondensedRow's own slots;
//   • CondensedRow declined for price variance: each row needs TWO value
//     slots (delta_pct AND impact) and TWO secondary lines (prev→curr+
//     units, AND affected_products) — CondensedRow's single-value/
//     single-secondary/single-pill contract can't hold that without
//     dropping information, so it stays hand-authored (same reasoning
//     OperationalEfficiencyScreen documented for its staff/hour rows);
//   • CondensedRow declined for the "what these figures mean" disclosure:
//     each row is a title + a paragraph of prose, not the icon/name/
//     value/pill row shape CondensedRow exists for;
//   • collapsed-empty: already satisfied — every zone (health banner,
//     descriptions, cost flow, both expense groups, variance, KPI grid)
//     is already gated on its own data existing, so nothing here can
//     render a run of dashes;
//   • PullToRefresh now wraps SurfaceStates (RangeBar stays outside both,
//     unchanged, so it keeps surviving loading/error/empty).
//
// This screen renders CONTENT ONLY — NavStackHost owns the header/back
// button (route meta in nav/routes.js).
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { GlassCard } from '../shell/LiquidGlass';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { reportingService } from '../../services/api';
import { useNav } from '../nav/NavStack';
import { rangeForPreset, toLocalISO, trendLabel } from '../nav/dateRange';
import { useRangePreset } from './rangeState';
import {
    normaliseKpi,
    fmtMoney,
    fmtMoneyAuto,
    fillDailyGaps,
    HeroValue,
    RangeBar,
    KpiGrid,
    InsightList,
    TrendChart,
    NarrativeCard,
    SurfaceStates,
} from './kit';
import {
    ShareBar, CondensedRow, PullToRefresh, ensureKitCss,
    IconCash,
} from '../shell/kit';

/* Screen-local chrome only — everything data-bearing comes from the kit
   (which also injects the shared .insk-* stylesheet, incl. .insk-num and
   the delta-pill classes reused by the hero below). */
const CSS = `
.cms-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;}
.cms-health{padding:16px 18px;margin-bottom:14px;
  border:1px solid color-mix(in srgb, var(--c-amber) 45%, transparent);}
.cms-health-head{display:flex;align-items:center;gap:8px;color:var(--c-amber);}
.cms-health-big{margin-top:8px;font-size:15px;font-weight:700;color:var(--c-ink);}
.cms-health-sub{margin-top:4px;font-size:12px;font-weight:500;color:var(--c-textLight);}
.cms-health-btn{display:flex;width:100%;min-height:44px;align-items:center;
  justify-content:center;gap:6px;margin-top:10px;padding:8px 0;background:none;
  border:0;border-radius:10px;font:inherit;font-size:12.5px;font-weight:700;
  color:var(--c-amber);cursor:pointer;touch-action:manipulation;}
.cms-health-btn:active{background:color-mix(in srgb, var(--c-amber) 10%, transparent);}
.cms-desc{padding:6px 18px;margin-bottom:14px;}
.cms-desc-toggle{display:flex;width:100%;min-height:44px;align-items:center;
  justify-content:space-between;gap:10px;background:none;border:0;margin:0;
  padding:6px 0;font:inherit;color:inherit;cursor:pointer;border-radius:10px;
  touch-action:manipulation;}
.cms-desc-toggle:active{background:color-mix(in srgb, var(--c-ink) 6%, transparent);}
.cms-desc-row{padding:8px 0 10px;
  border-block-start:1px solid color-mix(in srgb, var(--c-ink) 8%, transparent);}
.cms-desc-name{font-size:12px;font-weight:700;color:var(--c-ink);}
.cms-desc-text{margin-top:3px;font-size:12px;font-weight:500;line-height:1.55;
  color:var(--c-textLight);}
.cms-flow-note{margin-top:12px;font-size:12px;font-weight:500;line-height:1.6;
  color:var(--c-textLight);}
.cms-group-total{margin:-6px 6px 14px;font-size:11px;font-weight:500;
  color:var(--c-textLight);}
.cms-var{padding:16px 18px;margin-bottom:14px;}
.cms-var-row{padding:10px 0 12px;
  border-block-start:1px solid color-mix(in srgb, var(--c-ink) 8%, transparent);}
.cms-var-row:first-of-type{border-block-start:0;}
.cms-var-head{display:flex;align-items:center;gap:10px;}
.cms-var-name{flex:1;min-width:0;font-size:13.5px;font-weight:700;color:var(--c-ink);
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.cms-var-delta{display:inline-flex;align-items:center;gap:3px;flex:none;
  font-size:12px;font-weight:700;}
.cms-var-impact{flex:none;font-size:13px;font-weight:700;}
.cms-var-cap{margin-top:6px;font-size:11.5px;font-weight:500;color:var(--c-textLight);}
.cms-var-aff{margin-top:4px;font-size:11px;font-weight:500;color:var(--c-mutedLight);
  overflow:hidden;text-overflow:ellipsis;display:-webkit-box;
  -webkit-line-clamp:2;-webkit-box-orient:vertical;}
`;

/* Backend KPI names are English literals — map them to t() keys. cogs /
   operating_expenses / kpi_gross_margin already exist in the DB (shipped
   web pages use them); kpi_net_margin is registered by this branch. The
   raw name (with its '%') still reaches normaliseKpi via the kpi object,
   so percent detection is unaffected by the display label. */
const KPI_LABELS = {
    COGS: ['cogs', 'COGS'],
    'Gross Margin %': ['kpi_gross_margin', 'Gross Margin'],
    'Operating Expenses': ['operating_expenses', 'Operating Expenses'],
    'Net Margin %': ['kpi_net_margin', 'Net Margin'],
};

const MONEY_KPIS = new Set(['Revenue', 'COGS', 'Operating Expenses']);

/* The narrative's kpi_descriptions are keyed revenue / cogs / gross_margin /
   operating_expenses / net_margin (reporting_v2.py:1881). The web derives
   the lookup key as lower→underscore→strip-% and STOPS — "Gross Margin %"
   becomes "gross_margin_" and never matches, so its flip cards permanently
   show "no description available" (CostManagementPage.js:345). Trimming
   the trailing separators fixes it. Exported for its unit test. */
export function descKeyFor(name) {
    return (name || '')
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/%/g, '')
        .replace(/_+$/, '');
}

/* Cost-flow segments for ShareBar (W4 Task 16 — Task 3A contract, see file
   header). Exported for its own contract test, same pattern as descKeyFor
   above: proves this screen's EXACT segment shape (COGS/OpEx/Net Profit)
   behaves correctly under ShareBar's real rules — a negative net_profit
   (loss period; the P&L identity revenue = cogs + opex + net_profit puts
   no floor on it, reporting_v2.py:1450) must never disappear. */
export function buildFlowSegments(flow, currency, t) {
    const cogs = Number(flow && flow.cogs) || 0;
    const opex = Number(flow && flow.opex) || 0;
    const netProfit = Number(flow && flow.net_profit) || 0;
    return [
        {
            id: 'cogs',
            label: t('cogs', {}, 'COGS'),
            value: cogs,
            display: fmtMoneyAuto(cogs, currency),
            tone: 'red',
        },
        {
            id: 'opex',
            label: t('operating_expenses', {}, 'Operating Expenses'),
            value: opex,
            display: fmtMoneyAuto(opex, currency),
            tone: 'amber',
        },
        {
            id: 'net_profit',
            label: t('net_profit', {}, 'Net Profit'),
            value: netProfit,
            display: fmtMoneyAuto(netProfit, currency),
            tone: netProfit >= 0 ? 'green' : 'red',
        },
    ];
}

/* "Empty" = the period produced nothing to report: zero revenue, no daily
   rows and no expense categories. Zero revenue with expenses still renders
   (the cost story is decision-grade on its own). */
function isEmptyPayload(d) {
    const kpis = (d && d.kpis) || [];
    const rev = kpis.find((k) => k.name === 'Revenue');
    const sections = (d && d.sections) || {};
    const hasDaily = (sections.daily_costs || []).length > 0;
    const cats = sections.expense_categories || {};
    const hasExpenses = ((cats.overhead || []).length + (cats.daily || []).length) > 0;
    return !((rev && Number(rev.value)) || hasDaily || hasExpenses);
}

/* Small inline SVGs (no emoji). */
function WarnIcon() {
    return (
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
                d="M8 1.8 15 14H1L8 1.8Z M8 6v4 M8 11.6v.8"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinejoin="round"
                strokeLinecap="round"
            />
        </svg>
    );
}

function Chevron({ open }) {
    return (
        <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            aria-hidden="true"
            style={{ transform: open ? 'rotate(180deg)' : 'none', color: 'var(--c-textLight)' }}
        >
            <path d="M2 4.2 6 8.2 10 4.2" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function DeltaArrow({ up }) {
    return (
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
            <path d={up ? 'M5 1.5 L9 8 L1 8 Z' : 'M5 8.5 L1 2 L9 2 Z'} fill="currentColor" />
        </svg>
    );
}

export default function CostManagementScreen() {
    ensureKitCss();
    const auth = useAuth() || {};
    const currency = auth.getCurrency ? auth.getCurrency() : '£';
    const { t, language } = useLanguage();
    const { push } = useNav();

    // Shared across all Insights surfaces (M1) — survives pops and tab
    // switches; the setter coerces junk ids so rangeForPreset never throws.
    const [preset, setPreset] = useRangePreset();
    const range = useMemo(() => rangeForPreset(preset), [preset]);
    const [status, setStatus] = useState('loading'); // loading|error|empty|ready
    const [data, setData] = useState(null);
    const [narrative, setNarrative] = useState(null);
    const [narrativeStatus, setNarrativeStatus] = useState('loading');
    const [flaggedOpen, setFlaggedOpen] = useState(false);
    const [descOpen, setDescOpen] = useState(false);

    const startIso = toLocalISO(range.start);
    const endIso = toLocalISO(range.end);

    /* Main fetch — stale responses are dropped by sequence, so a quick
       range flick can never paint an older period over a newer one. */
    const seqRef = useRef(0);
    const load = useCallback(() => {
        seqRef.current += 1;
        const seq = seqRef.current;
        setStatus('loading');
        setFlaggedOpen(false);
        // Returned (W4 Task 16) so PullToRefresh can await it — its spinner
        // stays up until the fetch actually settles, success or failure.
        return reportingService.getCostsV2(startIso, endIso)
            .then((d) => {
                if (seq !== seqRef.current) return;
                setData(d);
                setStatus(isEmptyPayload(d) ? 'empty' : 'ready');
            })
            .catch(() => {
                if (seq === seqRef.current) setStatus('error');
            });
    }, [startIso, endIso]);
    useEffect(() => { load(); }, [load]);

    /* Narrative — an enhancement with its OWN status; its failure renders
       nothing and never touches the main surface state. */
    useEffect(() => {
        let stale = false;
        setNarrativeStatus('loading');
        reportingService.getCostNarrativeV2(startIso, endIso)
            .then((res) => {
                if (stale) return;
                setNarrative(res);
                setNarrativeStatus('ready');
            })
            .catch(() => {
                if (!stale) setNarrativeStatus('error');
            });
        return () => { stale = true; };
    }, [startIso, endIso]);

    const kpis = (data && data.kpis) || [];
    const sections = (data && data.sections) || {};

    /* ---- hero: Revenue + delta (normaliseKpi owns the quirks; rendering
       is the kit's HeroValue) ---- */
    const revKpi = kpis.find((k) => k.name === 'Revenue') || null;
    const revNorm = revKpi ? normaliseKpi(revKpi) : null;

    /* ---- KPI grid: the four non-hero KPIs. /costs always sends five, so
       the hero leaves an even grid — the odd-count guard still spans the
       last tile if the endpoint ever grows (Trap 2 of the brief). Percent
       KPIs ("Gross Margin %", "Net Margin %") carry NO is_percentage and
       NO unit — normaliseKpi detects them by name; a COGS/OpEx fall
       renders green via invert. ---- */
    const restKpis = revKpi ? kpis.filter((k) => k !== revKpi) : kpis;
    const gridItems = restKpis.map((k, i) => {
        const meta = KPI_LABELS[k.name];
        return {
            kpi: k,
            label: meta ? t(meta[0], {}, meta[1]) : k.name,
            format: MONEY_KPIS.has(k.name) ? 'currency' : 'number',
            caption: true,
            ...(restKpis.length % 2 === 1 && i === restKpis.length - 1 ? { span: 2 } : {}),
        };
    });

    /* ---- KPI descriptions (narrative enhancement): match through the
       FIXED key derivation, keep the web's name-literal fallbacks, and
       OMIT unmatched rows rather than rendering filler text. ---- */
    const kpiDescriptions = (narrative && narrative.kpi_descriptions) || {};
    const descRows = kpis
        .map((k) => {
            const meta = KPI_LABELS[k.name];
            const text = kpiDescriptions[descKeyFor(k.name)]
                || kpiDescriptions[k.name]
                || kpiDescriptions[(k.name || '').toLowerCase()];
            if (!text) return null;
            return {
                name: k.name === 'Revenue'
                    ? t('revenue', {}, 'Revenue')
                    : (meta ? t(meta[0], {}, meta[1]) : k.name),
                text,
            };
        })
        .filter(Boolean);

    /* ---- cost data health: hidden when there is no revenue or trust is
       total (the web page's rule, kept verbatim). ---- */
    const health = sections.cost_data_health || {};
    const showHealth = Number(health.total_revenue) > 0
        && Number(health.trusted_revenue_pct) < 100;
    const flagged = health.flagged_products || [];

    const openDetail = useCallback((code, name) => {
        push('product-detail', {
            code,
            name,
            range: { start: startIso, end: endIso },
        });
    }, [push, startIso, endIso]);

    /* ---- P&L cost flow: COGS / OpEx / Net Profit via ShareBar (W4 Task
       16 — see file header for the full Task 3A contract analysis). ---- */
    const flow = sections.cost_flow || {};
    const flowSegments = buildFlowSegments(flow, currency, t);
    const flowAriaLabel = t(
        'cost_flow_aria',
        {
            cogs: fmtMoneyAuto(flow.cogs, currency),
            opex: fmtMoneyAuto(flow.opex, currency),
            net: fmtMoneyAuto(flow.net_profit, currency),
        },
        'Cost flow: COGS {cogs}, Operating Expenses {opex}, Net Profit {net}',
    );
    const hasFlow = Number(flow.revenue) > 0;

    /* ---- expense groups: overhead and daily each rank against their OWN
       total (the web's two-column split) — never a shared denominator. ---- */
    const cats = sections.expense_categories || {};
    const groupItems = (rows) => {
        const totalCost = (rows || []).reduce((s, e) => s + (Number(e.total_cost) || 0), 0);
        return {
            total: totalCost,
            items: (rows || []).map((e) => ({
                label: e.category,
                value: Number(e.total_cost) || 0,
                pct: totalCost > 0 ? ((Number(e.total_cost) || 0) / totalCost) * 100 : 0,
            })),
        };
    };
    const overheadGroup = groupItems(cats.overhead);
    const dailyGroup = groupItems(cats.daily);

    /* ---- price variance: hidden entirely when empty (web rule) ---- */
    const variance = sections.price_variance || [];

    /* ---- daily cost pattern: interior closed days zero-filled, revenue
       FIRST (the <4-point stat-card fallback shows the first series) ---- */
    const dailyRows = fillDailyGaps(
        sections.daily_costs,
        (d) => ({ sales_date: d, revenue: 0, cogs: 0, opex: 0 }),
    );
    const seriesOf = (key) => dailyRows.map((r) => ({
        label: trendLabel(r, 'daily', language),
        value: Number(r[key]) || 0,
    }));
    const dailySeries = [
        { name: t('revenue', {}, 'Revenue'), points: seriesOf('revenue') },
        { name: t('cogs', {}, 'COGS'), points: seriesOf('cogs') },
        { name: t('opex', {}, 'OpEx'), points: seriesOf('opex') },
    ];

    return (
        <div className="cms-wrap">
            <style>{CSS}</style>
            {/* RangeBar sits OUTSIDE SurfaceStates so the range stays
                changeable from the error/empty states. */}
            <RangeBar
                value={preset}
                onChange={(id) => setPreset(id)}
            />
            <PullToRefresh onRefresh={load}>
                <SurfaceStates status={status} onRetry={load}>
                    {/* Hero — Revenue + delta vs previous period (kit HeroValue:
                        renders nothing when revNorm is null). */}
                    <HeroValue
                        label={t('revenue', {}, 'Revenue')}
                        norm={revNorm}
                        currency={currency}
                        testid="cost-hero"
                    />

                    {/* AI narrative — separate status; failure renders nothing. */}
                    <NarrativeCard
                        state={narrativeStatus}
                        text={narrative && narrative.narrative}
                        generatedAt={narrative && narrative.generated_at}
                    />

                    {/* Cost data health — how much of revenue has trustworthy
                        cost data; flagged rows push the (read-only) Product
                        Detail screen for the drill-down. */}
                    {showHealth && (
                        <GlassCard veil className="cms-health" data-testid="cost-health">
                            <div className="cms-health-head">
                                <WarnIcon />
                                <span className="insk-label" style={{ color: 'var(--c-amber)' }}>
                                    {t('cost_data_health', {}, 'Cost Data Health')}
                                </span>
                            </div>
                            <div className="cms-health-big insk-num">
                                {t(
                                    'trusted_revenue_pct',
                                    { pct: `${Number(health.trusted_revenue_pct || 0).toFixed(1)}%` },
                                    '{pct} of revenue has trusted cost data',
                                )}
                            </div>
                            <div className="cms-health-sub insk-num">
                                {t(
                                    'untrusted_revenue_note',
                                    { amount: fmtMoney(health.untrusted_revenue, currency) },
                                    '{amount} of revenue has no reliable cost behind it',
                                )}
                            </div>
                            {flagged.length > 0 && (
                                <>
                                    <button
                                        type="button"
                                        className="cms-health-btn"
                                        aria-expanded={flaggedOpen}
                                        onClick={() => setFlaggedOpen((v) => !v)}
                                    >
                                        {flaggedOpen
                                            ? t('hide_flagged_products', {}, 'Hide flagged products')
                                            : t('show_flagged_products', { n: flagged.length }, 'Show {n} flagged products')}
                                        <Chevron open={flaggedOpen} />
                                    </button>
                                    {/* W4 Task 16: CondensedRow — name/value/
                                        secondary/pill matches this row's shape
                                        exactly (units + cost-source badge, same
                                        pattern ProductIntelligenceScreen's full
                                        menu uses). Nothing dropped: units and
                                        cost_source both still render. */}
                                    {flaggedOpen && flagged.map((p) => (
                                        <CondensedRow
                                            key={p.product_code}
                                            icon={<WarnIcon />}
                                            tone={p.cost_source === 'recipe_partial' ? 'amber' : 'red'}
                                            name={p.product_name || p.product_code}
                                            value={fmtMoney(p.revenue, currency)}
                                            secondary={`${Number(p.units) || 0} ${t('units', {}, 'units')}`}
                                            pill={{
                                                label: p.cost_source === 'recipe_partial'
                                                    ? t('cost_source_recipe_partial', {}, 'Recipe missing ingredient costs')
                                                    : t('cost_source_unknown', {}, 'No cost data'),
                                                tone: p.cost_source === 'recipe_partial' ? 'amber' : 'red',
                                            }}
                                            onPress={() => openDetail(p.product_code, p.product_name || p.product_code)}
                                            testId={p.product_code}
                                        />
                                    ))}
                                </>
                            )}
                        </GlassCard>
                    )}

                    {/* KPI grid — quirks absorbed by the kit. */}
                    {gridItems.length > 0 && (
                        <KpiGrid currency={currency} items={gridItems} />
                    )}

                    {/* What these figures mean — the narrative's per-KPI
                        descriptions behind a 44pt disclosure (replaces the
                        web's flip cards; the broken key derivation is fixed in
                        descKeyFor above). */}
                    {descRows.length > 0 && (
                        <GlassCard veil className="cms-desc">
                            <button
                                type="button"
                                className="cms-desc-toggle"
                                data-testid="kpi-desc-toggle"
                                aria-expanded={descOpen}
                                onClick={() => setDescOpen((v) => !v)}
                            >
                                <span className="insk-label">
                                    {t('kpi_meaning_title', {}, 'What these figures mean')}
                                </span>
                                <Chevron open={descOpen} />
                            </button>
                            {descOpen && descRows.map((row) => (
                                <div className="cms-desc-row" key={row.name}>
                                    <div className="cms-desc-name">{row.name}</div>
                                    <div className="cms-desc-text insk-num">{row.text}</div>
                                </div>
                            ))}
                        </GlassCard>
                    )}

                    {/* P&L cost flow — where every pound of revenue went.
                        W4 Task 16: ShareBar (shell/kit) replaces StackedShareBar
                        — see file header for the full Task 3A contract
                        analysis. fmtMoneyAuto (M4, via buildFlowSegments):
                        COGS/OpEx also render as KPI tiles, which auto-compact
                        at ≥100k — the legend states the same figures the same
                        way ('£84.0K' in both, never '£84,000.00' two cards
                        below the tile). */}
                    {hasFlow && (
                        <>
                            <GlassCard veil className="cms-var" data-testid="cost-flow-card">
                                <div className="insk-label" style={{ marginBottom: 10 }}>
                                    {t('cost_flow', {}, 'Cost Flow')}
                                </div>
                                <ShareBar segments={flowSegments} ariaLabel={flowAriaLabel} />
                            </GlassCard>
                            {/* Ratios anchored to REVENUE, stated in words —
                                never a bare percentage (brief rule). */}
                            <GlassCard veil className="cms-var" data-testid="cost-flow-note" style={{ marginTop: -6 }}>
                                <div className="cms-flow-note insk-num" style={{ marginTop: 0 }}>
                                    {t(
                                        'cost_flow_narrative',
                                        {
                                            cogs_pct: (Number(flow.cogs_pct) || 0).toFixed(1),
                                            opex_pct: (Number(flow.opex_pct) || 0).toFixed(1),
                                            net_margin_pct: (Number(flow.net_margin_pct) || 0).toFixed(1),
                                        },
                                        'COGS takes {cogs_pct}% of revenue and operating expenses take {opex_pct}%, leaving a {net_margin_pct}% net margin.',
                                    )}
                                </div>
                            </GlassCard>
                        </>
                    )}

                    {/* Overhead vs daily expenses — each list ranks against its
                        OWN group total. W4 Task 16: CondensedRow rows (name,
                        cost value, "% of total" secondary — same wording the
                        web page used, CostManagementPage.js:478/522) replace
                        BarList; each group card is gated on its own
                        items.length (collapsed-empty rule — BarList's built-in
                        empty guard no longer applies). */}
                    {overheadGroup.items.length > 0 && (
                        <GlassCard veil className="insk-bars">
                            <div className="insk-label" style={{ marginBottom: 10 }}>
                                {t('overhead_expenses', {}, 'Overhead Expenses')}
                            </div>
                            {overheadGroup.items.map((item, i) => (
                                <CondensedRow
                                    key={item.label}
                                    icon={<IconCash />}
                                    tone="red"
                                    name={item.label}
                                    value={fmtMoney(item.value, currency)}
                                    secondary={`${item.pct.toFixed(1)}% ${t('of_total', {}, 'of total')}`}
                                    testId={`overhead-${i}`}
                                />
                            ))}
                        </GlassCard>
                    )}
                    {overheadGroup.items.length > 0 && (
                        <div className="cms-group-total insk-num" data-testid="group-total">
                            {`${t('total', {}, 'Total')}: ${fmtMoney(overheadGroup.total, currency)}`}
                        </div>
                    )}
                    {dailyGroup.items.length > 0 && (
                        <GlassCard veil className="insk-bars">
                            <div className="insk-label" style={{ marginBottom: 10 }}>
                                {t('daily_expenses', {}, 'Daily Expenses')}
                            </div>
                            {dailyGroup.items.map((item, i) => (
                                <CondensedRow
                                    key={item.label}
                                    icon={<IconCash />}
                                    tone="amber"
                                    name={item.label}
                                    value={fmtMoney(item.value, currency)}
                                    secondary={`${item.pct.toFixed(1)}% ${t('of_total', {}, 'of total')}`}
                                    testId={`daily-${i}`}
                                />
                            ))}
                        </GlassCard>
                    )}
                    {dailyGroup.items.length > 0 && (
                        <div className="cms-group-total insk-num" data-testid="group-total">
                            {`${t('total', {}, 'Total')}: ${fmtMoney(dailyGroup.total, currency)}`}
                        </div>
                    )}

                    {/* Price variance alerts — ingredient cost shifts ranked by
                        period impact; the whole card hides when empty. */}
                    {variance.length > 0 && (
                        <GlassCard veil className="cms-var" data-testid="cost-variance">
                            <div className="insk-label" style={{ marginBottom: 4 }}>
                                {t('price_variance_alerts', {}, 'Price Variance Alerts')}
                            </div>
                            {variance.map((v) => {
                                const up = Number(v.delta_pct) >= 0;
                                const impactUp = Number(v.impact) >= 0;
                                return (
                                    <div className="cms-var-row" key={v.inventory_id}>
                                        <div className="cms-var-head">
                                            <span className="cms-var-name">{v.name}</span>
                                            <span
                                                className="cms-var-delta insk-num"
                                                style={{ color: up ? 'var(--c-red)' : 'var(--c-green)' }}
                                            >
                                                <DeltaArrow up={up} />
                                                {`${Math.abs(Number(v.delta_pct) || 0).toFixed(1)}%`}
                                            </span>
                                            <span
                                                className="cms-var-impact insk-num"
                                                style={{ color: impactUp ? 'var(--c-red)' : 'var(--c-green)' }}
                                            >
                                                {`${impactUp ? '+' : ''}${fmtMoney(v.impact, currency)}`}
                                            </span>
                                        </div>
                                        <div className="cms-var-cap insk-num">
                                            {`${fmtMoney(v.prev_avg_cost, currency)} → ${fmtMoney(v.curr_avg_cost, currency)} · ${t(
                                                'units_consumed',
                                                { n: (Number(v.units_consumed) || 0).toLocaleString() },
                                                '{n} units consumed',
                                            )}`}
                                        </div>
                                        {(v.affected_products || []).length > 0 && (
                                            <div className="cms-var-aff">
                                                {`${t('affected_products', {}, 'Affected products')}: ${v.affected_products.join(', ')}`}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </GlassCard>
                    )}

                    {/* Daily cost pattern — revenue vs COGS vs OpEx per day as
                        ONE multi-series chart (revenue first: the <4-point
                        fallback stat shows the first series). */}
                    <TrendChart
                        title={t('daily_cost_patterns', {}, 'Daily Cost Patterns')}
                        ariaLabel={`${t('daily_cost_patterns', {}, 'Daily Cost Patterns')} — ${t(
                            'trend_points_count',
                            { count: dailyRows.length },
                            '{count} data points',
                        )}`}
                        series={dailySeries}
                        formatValue={(v) => fmtMoney(v, currency)}
                    />

                    {/* Insights — top-level data.insights (sibling of sections). */}
                    <InsightList items={(data && data.insights) || []} />
                </SurfaceStates>
            </PullToRefresh>
        </div>
    );
}
